import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSeo, breadcrumbSchema } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { CategoryPill } from "@/components/site/Bits";
import { accentFor } from "@/lib/verticals";
import api from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

const ORIGIN = process.env.REACT_APP_BACKEND_URL || "";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/insights/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: article ? article.seo_title || article.title : "Insight",
    description: article ? article.seo_description || article.excerpt : "",
    path: `/insights/${slug}`,
    image: article?.cover_image || null,
    schema: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Organization", name: article.author || "ThinkStack" },
          datePublished: article.published_at,
          image: article.cover_image || undefined,
          mainEntityOfPage: `${ORIGIN}/insights/${slug}`,
        }
      : null,
  });

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-deep-navy">Article not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-restrained-blue">
          <ArrowLeft size={16} /> Back to Insights
        </Link>
      </div>
    );
  }

  if (!article) {
    return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-restrained-blue" /></div>;
  }

  const accent = accentFor(article.category);

  return (
    <article>
      {/* Accent band hero */}
      <div style={{ backgroundColor: accent.color }} className="text-white">
        <div className="mx-auto max-w-4xl px-6 md:px-8 pt-14 md:pt-16 pb-16 md:pb-20">
          <Link to="/insights" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors">
            <ArrowLeft size={15} /> Insights
          </Link>
          <Reveal>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-1.5 bg-white/15 text-white mt-8">
              {article.category}
            </span>
            <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02] mt-5">{article.title}</h1>
            <div className="mt-6 flex items-center gap-4 text-sm text-white/70">
              <span>{article.author}</span>{article.read_time && <><span>·</span><span>{article.read_time}</span></>}
            </div>
          </Reveal>
        </div>
      </div>

      {article.cover_image && (
        <div className="mx-auto max-w-5xl px-6 md:px-8 -mt-10 md:-mt-12 relative z-10">
          <div className="aspect-[16/8] overflow-hidden border border-[#E5E5E5] bg-off-white shadow-[0_20px_60px_-24px_rgba(10,25,47,0.4)]">
            <img src={article.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-6 md:px-8 py-14 md:py-20">
        <p className="font-serif text-2xl md:text-3xl text-deep-navy leading-snug mb-12 pb-12 border-b-2" style={{ borderColor: "#5C2D50" }}>{article.excerpt}</p>
        <div className="editorial-body">
          {(article.body || []).map((block, i) => {
            if (block.type === "heading")
              return <h2 key={i} className="font-serif text-2xl md:text-3xl text-deep-navy mt-12 mb-5">{block.text}</h2>;
            if (block.type === "image")
              return (
                <figure key={i} className="my-10 -mx-2 md:-mx-8">
                  <div className="border border-[#E5E5E5] overflow-hidden bg-off-white">
                    <img src={block.src} alt={block.caption || ""} loading="lazy" className="w-full h-auto" />
                  </div>
                  {block.caption && <figcaption className="mt-3 text-sm text-charcoal/50 font-mono px-2">{block.caption}</figcaption>}
                </figure>
              );
            return <p key={i}>{block.text}</p>;
          })}
        </div>

        <div className="mt-16 pt-10 border-t border-[#E5E5E5]">
          <p className="font-serif text-2xl text-deep-navy">Have a process worth redesigning?</p>
          <Link to="/contact" data-testid="article-cta"
                className="mt-5 inline-flex items-center gap-2 text-white px-7 py-3.5 text-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: "#5C2D50" }}>
            Discuss a Transformation →
          </Link>
        </div>
      </div>
    </article>
  );
}
